import moment from 'moment';
/**
 * Date utility functions for consistent date formatting across the application
 */

/**
 * Formats a date to ISO8601 format (YYYY-MM-DD)
 * @param {Date|string|number} date - The date to format
 * @returns {string|null} - The formatted date string or null if invalid
 */
export function formatDateToISO8601(date) {
  if (!date) return null;

  if (date instanceof Date) {
    return date.toISOString().split('T')[0];
  }

  if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    const testDate = new Date(date);
    if (!isNaN(testDate.getTime())) {
      // Convert back to ISO string to ensure normalization
      return testDate.toISOString().split('T')[0];
    }
  }

  if (typeof date === 'string') {
    const parsedDate = new Date(date);
    if (!isNaN(parsedDate.getTime())) {
      return parsedDate.toISOString().split('T')[0];
    }
  }

  const fallbackDate = new Date(date);
  if (!isNaN(fallbackDate.getTime())) {
    return fallbackDate.toISOString().split('T')[0];
  }

  return null;
}

/**
 * Validates if a string is in ISO8601 date format (YYYY-MM-DD)
 * @param {string} dateString - The date string to validate
 * @returns {boolean} - True if the string is in valid ISO8601 date format
 */
export function isValidISO8601Date(dateString) {
  if (typeof dateString !== 'string') return false;

  const iso8601Regex = /^\d{4}-\d{2}-\d{2}$/;
  if (!iso8601Regex.test(dateString)) return false;

  const date = new Date(dateString);
  return (
    !isNaN(date.getTime()) && date.toISOString().split('T')[0] === dateString
  );
}

/**
 * Gets the current date in ISO8601 format
 * @returns {string} - Current date in YYYY-MM-DD format
 */
export function getCurrentDateISO8601() {
  return new Date().toISOString().split('T')[0];
}

/**
 * Formats a time string to 24-hour format (HH:mm)
 * @param {string} timeString - The time string in various formats (e.g., "2:30 PM", "10:15 AM", "14:30", "9:00am")
 * @returns {string|null} - The formatted time in 24-hour format (HH:mm) or null if invalid
 */
export function formatTimeTo24Hour(timeString) {
  if (!timeString) return null;

  timeString = timeString.trim();

  // Handle 24-hour format (already in correct format)
  if (/^\d{1,2}:\d{2}$/.test(timeString)) {
    const [hours, minutes] = timeString.split(':');
    const hourNum = parseInt(hours, 10);
    const minuteNum = parseInt(minutes, 10);

    if (hourNum >= 0 && hourNum <= 23 && minuteNum >= 0 && minuteNum <= 59) {
      return `${hourNum.toString().padStart(2, '0')}:${minuteNum.toString().padStart(2, '0')}`;
    }
  }

  // Handle 12-hour format with AM/PM
  if (/AM|PM/i.test(timeString)) {
    const formats = [
      'h:mm A',
      'hh:mm A',
      'h:mm a',
      'hh:mm a',
      'h:mmA',
      'hh:mmA',
      'h:mma',
      'hh:mma',
      'h:mm:ss A',
      'hh:mm:ss A',
      'h:mm:ss a',
      'hh:mm:ss a',
      'h:mm:ssA',
      'hh:mm:ssA',
      'h:mm:ssa',
      'hh:mm:ssa',
    ];

    const momentTime = moment(timeString, formats);
    if (momentTime.isValid()) {
      // Validate that it's a proper 12-hour time (hours 1-12)
      const hour = momentTime.hour();
      const isAM = /AM/i.test(timeString);
      const isPM = /PM/i.test(timeString);

      // For AM: hours should be 0-11 (0 = 12 AM, 1-11 = 1-11 AM)
      // For PM: hours should be 12-23 (12 = 12 PM, 13-23 = 1-11 PM)
      if (
        (isAM && hour >= 0 && hour <= 11) ||
        (isPM && hour >= 12 && hour <= 23)
      ) {
        return momentTime.format('HH:mm');
      }
    }
  }

  return null;
}
